import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCircle, Mail, Phone, Building2, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useContactStore } from '@/store/contactStore';
import { useActivityStore } from '@/store/activityStore';
import { ActivityTimeline } from '@/components/activities/ActivityTimeline';

export function ContactDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedContact, fetchContactById } = useContactStore();
  const { fetchActivitiesByEntity } = useActivityStore();
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (id) {
        setIsLoading(true);
        await fetchContactById(id);
        const acts = await fetchActivitiesByEntity('contact', id);
        setActivities(acts);
        setIsLoading(false);
      }
    };
    loadData();
  }, [id, fetchContactById, fetchActivitiesByEntity]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!selectedContact) {
    return <div className="p-6">Contact not found</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/contacts')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <UserCircle className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{selectedContact.first_name} {selectedContact.last_name}</h1>
              {selectedContact.is_primary && <Badge className="bg-green-100 text-green-800" variant="secondary">Primary</Badge>}
            </div>
            <p className="text-muted-foreground">{selectedContact.job_title || 'No title'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="activities">
            <TabsList>
              <TabsTrigger value="activities">Activities</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="activities" className="mt-4">
              <ActivityTimeline activities={activities} />
            </TabsContent>
            <TabsContent value="notes" className="mt-4">
              <p className="text-muted-foreground">{selectedContact.notes || 'No notes added'}</p>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {selectedContact.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">{selectedContact.email}</a>
                </div>
              )}
              {selectedContact.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedContact.phone}</span>
                </div>
              )}
              {selectedContact.mobile && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedContact.mobile} (Mobile)</span>
                </div>
              )}
              {selectedContact.linkedin_url && (
                <div className="flex items-center gap-3">
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                  <a href={selectedContact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">LinkedIn</a>
                </div>
              )}
              {selectedContact.company_name && (
                <div className="flex items-center gap-3 pt-4 border-t">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedContact.company_name}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
